import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy, Timestamp, deleteDoc, doc } from 'firebase/firestore';
import { db, restoreBahanStock } from '../../firebase';
import './LaporanPenggunaanBahan.css';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import { 
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress
} from '@mui/material';

// Helper to format Firestore Timestamp to readable date string
const formatDate = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleDateString('id-ID');
  }
  return typeof timestamp === 'string' ? timestamp : 'N/A';
};

// Helper to get the start of a day for a date string 'YYYY-MM-DD'
const getStartOfDayTimestamp = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(date);
};

// Helper to get the end of a day for a date string 'YYYY-MM-DD'
const getEndOfDayTimestamp = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  date.setHours(23, 59, 59, 999);
  return Timestamp.fromDate(date);
};

const LaporanPenggunaanBahan = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bahanList, setBahanList] = useState([]);
  const [selectedBahan, setSelectedBahan] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [loadingBahan, setLoadingBahan] = useState(false);

  // Fetch bahan list
  const fetchBahanList = async () => {
    setLoadingBahan(true);
    try {
      const bahanRef = collection(db, 'bahan');
      const querySnapshot = await getDocs(bahanRef);
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBahanList(data.sort((a, b) => a.nama.localeCompare(b.nama)));
    } catch (err) {
      console.error("Error fetching materials:", err);
      setError('Gagal memuat daftar bahan.');
    } finally {
      setLoadingBahan(false);
    }
  };

  useEffect(() => {
    fetchBahanList();
  }, []);

  // Fetch requests based on filters
  const fetchRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const requestsRef = collection(db, 'permintaan_bahan');
      
      // Build query based on filters
      let q = query(requestsRef, where('status', 'in', ['approved', 'admin_approved']));
      
      // Add bahan filter if selected
      if (selectedBahan) {
        q = query(q, where('namaBahan', '==', selectedBahan.nama));
      }
      
      // Add date sorting
      q = query(q, orderBy('tanggalPermintaan', 'desc'));
      
      const querySnapshot = await getDocs(q);
      let data = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      
      // Filter by date range in memory since Firestore doesn't support multiple range operators
      if (filterStartDate || filterEndDate) {
        const startTimestamp = filterStartDate ? getStartOfDayTimestamp(filterStartDate).toDate() : new Date(0);
        const endTimestamp = filterEndDate ? getEndOfDayTimestamp(filterEndDate).toDate() : new Date();
        
        data = data.filter(req => {
          const reqDate = req.tanggalPermintaan.toDate();
          return reqDate >= startTimestamp && reqDate <= endTimestamp;
        });
      }
      
      setRequests(data);
    } catch (err) {
      console.error("Error fetching material requests report:", err);
      setError('Gagal memuat data laporan penggunaan bahan.');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on component mount
  useEffect(() => {
    fetchRequests();
  }, []);

  // Handle filter application
  const handleApplyFilter = () => {
    fetchRequests();
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!requestToDelete) return;
    
    setDeletingId(requestToDelete);
    setError('');
    try {
      // Find the request details
      const requestToDeleteData = requests.find(req => req.id === requestToDelete);
      if (!requestToDeleteData) {
        throw new Error('Data permintaan tidak ditemukan.');
      }

      // If request was approved, restore the stock first
      if (requestToDeleteData.status === 'approved' || requestToDeleteData.status === 'admin_approved') {
        await restoreBahanStock(
          requestToDeleteData.namaBahan,
          requestToDeleteData.jumlahDiminta,
          requestToDeleteData.satuan
        );
      }

      // Then delete the request
      const requestRef = doc(db, 'permintaan_bahan', requestToDelete);
      await deleteDoc(requestRef);
      await fetchRequests(); // Refresh the list
      setShowConfirmDialog(false);
      setRequestToDelete(null);
    } catch (err) {
      console.error("Error deleting request:", err);
      setError(`Gagal menghapus data permintaan: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Handle delete request
  const handleDelete = (id) => {
    setRequestToDelete(id);
    setShowConfirmDialog(true);
  };

  // Generate PDF report
  const handleGeneratePDF = () => {
    try {
      const doc = new jsPDF();
      
      // Title
      doc.setFontSize(16);
      doc.text('Laporan Penggunaan Bahan', 14, 15);
      
      // Date Range
      doc.setFontSize(10);
      const dateRange = filterStartDate && filterEndDate
        ? `Periode: ${new Date(filterStartDate).toLocaleDateString('id-ID')} - ${new Date(filterEndDate).toLocaleDateString('id-ID')}`
        : 'Semua Periode';
      doc.text(dateRange, 14, 25);

      // Group materials by name and calculate totals
      const materialUsage = {};
      const approvedRequests = requests.filter(req => req.status === 'approved' || req.status === 'admin_approved');
      
      approvedRequests.forEach(req => {
        const key = req.namaBahan;
        if (!materialUsage[key]) {
          materialUsage[key] = {
            total: 0,
            satuan: req.satuan,
            requests: []
          };
        }
        materialUsage[key].total += req.jumlahDiminta;
        materialUsage[key].requests.push(req);
      });

      // Summary Table
      doc.setFontSize(12);
      doc.text('Ringkasan Penggunaan Bahan', 14, 35);

      const summaryTableBody = Object.entries(materialUsage).map(([bahan, data]) => [
        bahan,
        `${data.total} ${data.satuan}`
      ]);

      autoTable(doc, {
        startY: 40,
        head: [['Nama Bahan', 'Total Penggunaan']],
        body: summaryTableBody,
        theme: 'grid',
        headStyles: { fillColor: [22, 160, 133] }
      });

      // Detailed Table
      doc.setFontSize(12);
      doc.text('Detail Penggunaan Bahan', 14, doc.lastAutoTable.finalY + 15);

      const detailedTableBody = approvedRequests.map(req => [
        req.namaBahan,
        req.namaUser,
        req.dosenPengampuId ? 'Dosen Pengampu' : (req.dosenPembimbingId ? 'Dosen Pembimbing' : 'N/A'),
        `${req.jumlahDiminta} ${req.satuan}`,
        formatDate(req.tanggalPermintaan),
        req.tujuanPenggunaan || 'N/A'
      ]);

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 20,
        head: [['Nama Bahan', 'Peminta', 'Dosen Terkait', 'Jumlah', 'Tanggal', 'Tujuan']],
        body: detailedTableBody,
        theme: 'grid',
        headStyles: { fillColor: [22, 160, 133] },
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 35 },
          2: { cellWidth: 35 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 35 }
        }
      });

      // Save the PDF
      doc.save(`Laporan_Penggunaan_Bahan_${Date.now()}.pdf`);

    } catch (err) {
      console.error("Error generating PDF:", err);
      setError('Gagal membuat file PDF.');
    }
  };

  return (
    <div className="laporan-bahan-container">
      <h2>Laporan Penggunaan Bahan</h2>

      {/* Filter Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filter Laporan</Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Material Search */}
          <Box sx={{ minWidth: 300 }}>
            <Autocomplete
              options={bahanList}
              getOptionLabel={(option) => 
                `${option.nama}${option.kode ? ` - ${option.kode}` : ''}`
              }
              onChange={(event, newValue) => {
                setSelectedBahan(newValue);
                handleApplyFilter();
              }}
              value={selectedBahan}
              loading={loadingBahan}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pilih Bahan"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingBahan && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">
                      {option.nama}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.kode && `Kode: ${option.kode}`}
                      {option.jenis && ` | Jenis: ${option.jenis}`}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Box>

          {/* Date Filters */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              type="date"
              label="Dari Tanggal"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="Sampai Tanggal"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <button onClick={handleApplyFilter} disabled={loading}>
              Terapkan Filter
            </button>
            <button 
              onClick={handleGeneratePDF} 
              disabled={loading || requests.length === 0}
              className="btn-generate-pdf"
            >
              Generate PDF
            </button>
          </Box>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="confirmation-dialog">
          <div className="confirmation-content">
            <h3>Konfirmasi Penghapusan</h3>
            <p>Apakah Anda yakin ingin menghapus data ini?</p>
            <div className="confirmation-buttons">
              <button 
                onClick={handleDeleteConfirm}
                disabled={deletingId !== null}
                className="confirm-button"
              >
                {deletingId ? 'Menghapus...' : 'Ya, Hapus'}
              </button>
              <button 
                onClick={() => {
                  setShowConfirmDialog(false);
                  setRequestToDelete(null);
                }}
                className="cancel-button"
                disabled={deletingId !== null}
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading-message">Memuat laporan...</div>
      ) : (
        <div className="laporan-bahan-table-container">
          <table className="laporan-bahan-table">
            <thead>
              <tr>
                <th>Nama Peminta</th>
                <th>Nama Bahan</th>
                <th>Jumlah Diminta</th>
                <th>Satuan</th>
                <th>Tgl Permintaan</th>
                <th>Status</th>
                <th>Tujuan</th>
                <th>Dosen Terkait</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id}>
                  <td>{req.namaUser || 'N/A'}</td>
                  <td>{req.namaBahan || 'N/A'}</td>
                  <td>{req.jumlahDiminta || 'N/A'}</td>
                  <td>{req.satuan || 'N/A'}</td>
                  <td>{formatDate(req.tanggalPermintaan)}</td>
                  <td>
                    <span className={`status-badge status-${req.status || 'unknown'}`}>
                      {req.status || 'Unknown'}
                    </span>
                  </td>
                  <td>{req.tujuanPenggunaan || 'N/A'}</td>
                  <td>
                    {req.dosenPengampuId ? 'Dosen Pengampu' : (req.dosenPembimbingId ? 'Dosen Pembimbing' : 'N/A')}
                    {req.approvalHistory && req.approvalHistory.length > 0 && (
                      <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                        {req.approvalHistory.map((history, idx) => (
                          <div key={idx}>
                            {history.approver.name} ({history.approver.role}) - {history.action === 'approved' ? 'Menyetujui' : 'Menolak'} pada {formatDate(history.timestamp)}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => handleDelete(req.id)}
                      disabled={deletingId === req.id}
                      className="delete-button"
                    >
                      {deletingId === req.id ? 'Menghapus...' : 'Hapus'}
                    </button>
                  </td>
                </tr>
              ))}
              {requests.length === 0 && !loading && (
                <tr>
                  <td colSpan="9" style={{ textAlign: 'center' }}>Tidak ada data laporan untuk filter yang dipilih.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LaporanPenggunaanBahan;
